import * as THREE from 'three';
import { clone as cloneSkeleton } from 'three/addons/utils/SkeletonUtils.js';
import EnemyMissile, { EnemyMissileState } from './EnemyMissile.js';
import { SMOKE_TRAILS, ENEMY_MISSILE } from '../core/SimConfig.js';

const _segDelta = new THREE.Vector3();

/**
 * Centralized lifecycle manager for in-flight enemy missiles.
 *
 *   - Owns the master mesh template and clones a fresh mesh per launch.
 *   - Tracks all active missiles regardless of which platform fired them.
 *   - Each frame: ticks missile physics, runs swept hit detection against
 *     the player (HitVolume if present, fall-back proximity already handled
 *     inside EnemyMissile.update), runs terrain raycasts, drains explosion +
 *     audio events, and prunes DEAD missiles.
 *   - Smoke-trail handles spawned on launch, advanced each tick, released
 *     on death so the ribbon ages out cleanly.
 *
 * Parallels WeaponSystem on the player side, minus the bullet pool.
 */
export default class EnemyMissileSystem {
  /**
   * @param {{
   *   scene: THREE.Scene,
   *   missileModel: THREE.Object3D | null,
   *   particleSystem: import('../fx/ParticleSystem.js').default | null,
   *   smokeTrailSystem: import('../fx/SmokeTrailSystem.js').default | null,
   * }} opts
   */
  constructor({ scene, missileModel, particleSystem = null, smokeTrailSystem = null }) {
    this.scene = scene;
    this.particleSystem = particleSystem;
    this.smokeTrailSystem = smokeTrailSystem;
    this._masterMesh = missileModel ?? null;

    /** @type {EnemyMissile[]} */
    this._active = [];

    // Drained by AudioManager (same shape as WeaponSystem's events).
    this._launches = [];   // { x, y, z }
    this._explosions = []; // { name, x, y, z }

    // Optional MissionStats sink (combat scenarios) — every enemy missile that
    // detonates without striking the player counts as an evaded missile.
    this.stats = null;
  }

  drainLaunches()    { const o = this._launches;   this._launches = [];   return o; }
  drainExplosions()  { const o = this._explosions; this._explosions = []; return o; }

  /** Live missiles, for read-only consumers (RWR scope). Do not mutate. */
  get activeMissiles() { return this._active; }

  /**
   * Spawn a fresh missile at the launcher pose and lock it onto `target`.
   * Returns true on success, false if the missile model wasn't loaded.
   *
   * @param {THREE.Vector3} position
   * @param {THREE.Quaternion} quaternion
   * @param {THREE.Vector3} velocity
   * @param {object} target
   * @param {object|null} source  launching platform — the RWR rings an emitter
   *                              for as long as one of its rounds is in the air
   */
  launch(position, quaternion, velocity, target, source = null) {
    if (!this._masterMesh) return false;

    const mesh = cloneSkeleton(this._masterMesh);
    mesh.traverse((child) => {
      if (child.isMesh) {
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });

    const missile = new EnemyMissile({ modelMesh: mesh, scene: this.scene });
    missile.fire(position, quaternion, velocity, target);
    missile.source = source;

    if (this.smokeTrailSystem) {
      missile.smokeTrailHandle = this.smokeTrailSystem.spawn(SMOKE_TRAILS.presets.aim9);
    }
    this._launches.push({ x: missile.position.x, y: missile.position.y, z: missile.position.z });
    target?.onAIM9Launched?.();
    this._active.push(missile);
    return true;
  }

  update(dt, terrainCollider) {
    for (let i = this._active.length - 1; i >= 0; i--) {
      const m = this._active[i];
      if (m.state !== EnemyMissileState.FIRED) {
        this._active.splice(i, 1);
        continue;
      }

      m.update(dt);
      if (m.state !== EnemyMissileState.FIRED) {
        // Killed inside update() — internal fuse or lifetime expiry.
        this._releaseTrail(m);
        if (m.detonation) {
          this._explode(m.position.x, m.position.y, m.position.z, m.detonation.isAircraftHit);
          m.detonation = null;
        }
        this._active.splice(i, 1);
        continue;
      }

      if (this.smokeTrailSystem && m.smokeTrailHandle >= 0) {
        this.smokeTrailSystem.advance(
          m.smokeTrailHandle,
          m.position.x, m.position.y, m.position.z,
          m.velocity.x, m.velocity.y, m.velocity.z,
        );
      }

      // Swept segment from previous → current position for hit tests.
      _segDelta.set(
        m.position.x - m.prevX,
        m.position.y - m.prevY,
        m.position.z - m.prevZ,
      );
      const segLen = _segDelta.length();
      if (segLen < 0.001) continue;

      // Player body hit-test via HitVolume (if the A-10 has one). Stays armed
      // even while the target is dispensing flares: a flare-decoyed missile
      // aims off the tail and usually misses, but flying into its path should
      // still connect — see EnemyMissile for the matching proximity fuse.
      const target = m.target;
      if (target?.hitVolume) {
        const tVel = target.flight ? target.flight.velocity : target.velocity;
        const tvx = tVel ? tVel.x * dt : 0;
        const tvy = tVel ? tVel.y * dt : 0;
        const tvz = tVel ? tVel.z * dt : 0;
        const ox = m.prevX + tvx;
        const oy = m.prevY + tvy;
        const oz = m.prevZ + tvz;
        const h = target.hitVolume.intersectsSegment(
          ox, oy, oz,
          _segDelta.x - tvx, _segDelta.y - tvy, _segDelta.z - tvz,
        );
        if (h) {
          const tHit = h.t;
          const hx = m.prevX + _segDelta.x * tHit;
          const hy = m.prevY + _segDelta.y * tHit;
          const hz = m.prevZ + _segDelta.z * tHit;
          target.onProjectileHit?.(hx, hy, hz, ENEMY_MISSILE.damage, 'enemyMissile');
          this._explode(hx, hy, hz, true);
          this._releaseTrail(m);
          m.kill();
          this._active.splice(i, 1);
          continue;
        }
      }

      if (terrainCollider) {
        const th = terrainCollider.raycast(
          m.prevX, m.prevY, m.prevZ,
          _segDelta.x, _segDelta.y, _segDelta.z,
          segLen,
          segLen,
        );
        if (th) {
          this._explode(th.x, th.y, th.z, false);
          this._releaseTrail(m);
          m.kill();
          this._active.splice(i, 1);
          continue;
        }
      }
    }
  }

  _explode(x, y, z, isAircraftHit) {
    this._explosions.push({ name: 'Explosion_AIM9', x, y, z, isAircraftHit });
    if (!isAircraftHit) this.stats?.recordMissileEvaded();
    if (!this.particleSystem) return;
    this.particleSystem.spawnExplosion(x, y, z, isAircraftHit ? undefined : { debrisCount: 0 });
  }

  _releaseTrail(m) {
    if (!this.smokeTrailSystem) return;
    if (m.smokeTrailHandle === undefined || m.smokeTrailHandle < 0) return;
    this.smokeTrailSystem.release(m.smokeTrailHandle);
    m.smokeTrailHandle = -1;
  }

  dispose() {
    // Don't release trail handles here — SmokeTrailSystem.dispose() runs
    // earlier in App._teardown and zeroes its slot array. Trying to call
    // release() into the dead system throws. The trail slots are reclaimed
    // wholesale when the smoke system tears down.
    for (let i = 0; i < this._active.length; i++) {
      this._active[i].dispose();
    }
    this._active.length = 0;
  }
}
